/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  TextField,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
  Autocomplete,
  alpha,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import proxy from '@/services/ProxyService';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';

const Templates = ({
  app,
  deployment,
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
  data,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const { weclappInterface } = data;
  const [taxModels, setTaxModels] = useState([]);

  const [fields, setFields] = useState({
    groupBy: deployment?.config?.groupBy || '',
    travelMode: deployment?.config?.travelMode || '',
    distanceArticle: deployment?.config?.distanceArticle || '',
    durationArticle: deployment?.config?.durationArticle || '',
    travelArticle: deployment?.config?.travelArticle || '',
    ticketArticle: deployment?.config?.ticketArticle || '',
    tags: deployment?.config?.tags || [],
    euTaxModel: deployment?.config?.euTaxModel || '',
    travelInvoicePosition: null,
    listUnbillable: true,
    primary: deployment?.config?.primary || '',
    oneArticle: deployment?.config?.oneArticle || '',
    onePosition: deployment?.config?.onePosition || '',
    pricesFrom: deployment?.config?.pricesFrom || '',
    tanssDisplayName: deployment?.config?.tanssDisplayName || '',
    servicePeriods: deployment?.config?.servicePeriods || '',
    selectedFieldForInvoiceId:
      deployment?.config?.selectedFieldForInvoiceId || '',
    commission: deployment?.config?.commission || '',
    transferPdfs: deployment?.config?.transferPdfs || false,
    onlyBillableMaterial: deployment?.config?.onlyBillableMaterial || true,
  });

  const [serviceUnit, setServiceUnit] = useState(
    deployment?.config?.service?.unit || '',
  );

  const handleChange = (field) => (event) => {
    setFields((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleUnitChange = (event) => {
    setServiceUnit(event.target.value);
  };

  const handleTagsChange = (_, newValue) => {
    setFields((prev) => ({
      ...prev,
      tags: Array.isArray(newValue) ? newValue : [],
    }));
  };

  const handleSave = () => {
    next({
      ...fields,
      tempServiceUnit: serviceUnit,
    });
  };

  const fetchTaxModels = async () => {
    try {
      const { data: taxData } = await proxy.get(`${weclappInterface}/tax`);
      setTaxModels(taxData.result || []);
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          message="Die Steuermodelle konnten nicht geladen werden"
        />,
      );
    }
  };

  useEffect(() => {
    fetchTaxModels();
  }, []);

  const fieldWidth = isMdUp ? '48%' : '100%';

  const showUnitAndTicketFields =
    fields.groupBy === 'none' || fields.groupBy === 'ticket';
  const showTravelFields = fields.groupBy !== 'onePosition';

  const formRow = (fieldA, fieldB) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMdUp ? 'row' : 'column',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
      }}
    >
      {fieldA}
      {fieldB}
    </Box>
  );

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<LinkIcon color="primary" />}
        title={<Typography variant="h6">Rechnungs-Konfiguration</Typography>}
        subheader={`Für ${app?.title || deployment?.title}`}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Divider />

      <CardContent
        sx={{ flex: 1, pr: 1, overflowY: 'visible', overflowX: 'auto' }}
      >
        <Typography variant="body1" sx={{ mb: 3 }}>
          Konfigurieren Sie die Rechnungseinstellungen
        </Typography>

        {formRow(
          <TextField
            select
            fullWidth
            label="Abrechnungsmodus"
            value={fields.groupBy}
            onChange={handleChange('groupBy')}
            sx={{ width: fieldWidth }}
            helperText="Wie sollen die Positionen auf der Rechnung erscheinen? Eine Position pro Ticket, pro Techniker oder pro Leistung? Oder generell nur eine Position?"
          >
            <MenuItem value="none">Leistungen</MenuItem>
            <MenuItem value="ticket">Tickets</MenuItem>
            <MenuItem value="technician">Techniker</MenuItem>
            <MenuItem value="onePosition">Eine Position</MenuItem>
          </TextField>,
          <TextField
            select
            fullWidth
            label="Preise aus"
            value={fields.primary}
            onChange={handleChange('primary')}
            sx={{ width: fieldWidth }}
            helperText="Sollen die Preise aus TANSS kommen oder durch weclapp berechnet werden? ACHTUNG: weclapp funktioniert nur bei einer Abrechnung auf Leistungs-Basis."
          >
            <MenuItem value="tanss">TANSS</MenuItem>
            <MenuItem value="weclapp">weclapp</MenuItem>
          </TextField>,
        )}

        {showUnitAndTicketFields &&
          formRow(
            <TextField
              select
              fullWidth
              label="Einheit für Leistungen"
              value={fields.unit}
              onChange={handleUnitChange}
              sx={{ width: fieldWidth }}
              helperText="Welche Einheit soll für die Leistungen verwendet werden?
             Ticket-Positionen haben immer die Einheit Stück und die Menge 1, da sie neben Arbeitszeit auch Fahrten und Pauschalen beinhalten können."
            >
              <MenuItem value="ae">Abrechnungseinheiten</MenuItem>
              <MenuItem value="h">Stunden</MenuItem>
              <MenuItem value="min">Minuten</MenuItem>
            </TextField>,
            <TextField
              fullWidth
              label="Ticket-Artikel"
              value={fields.ticketArticle}
              onChange={handleChange('ticketArticle')}
              sx={{ width: fieldWidth }}
              helperText="Artikelnummer für den Ticket-Artikel. Dieser wird bei pauschal abgerechneten Tickets oder bei der Gruppierung nach Tickets verwendet."
            />,
          )}

        {showTravelFields &&
          formRow(
            <TextField
              select
              fullWidth
              label="Fahrtabrechnung"
              value={fields.travelMode}
              onChange={handleChange('travelMode')}
              sx={{ width: fieldWidth }}
              helperText="Wie sollen die Fahrten auf der Rechnung erscheinen?"
            >
              <MenuItem value="include">Nicht einzeln ausweisen</MenuItem>
              <MenuItem value="onePosition">
                In einer Sammelposition ausweisen
              </MenuItem>
              <MenuItem value="includeDuration">
                Nur Kilometer und Zone einzeln ausweisen
              </MenuItem>
              <MenuItem value="single">Alles einzeln ausweise</MenuItem>
            </TextField>,
            <TextField
              fullWidth
              label="Kilometer-Artikel"
              value={fields.distanceArticle}
              onChange={handleChange('distanceArticle')}
              sx={{ width: fieldWidth }}
              helperText="Artikelnummer für den Kilometer-Artikel. Dieser wird zur Abrechnung von Fahrten verwendet."
            />,
          )}

        {formRow(
          <TextField
            select
            fullWidth
            label="Steuermodell für EU-Kunden"
            value={fields.euTaxModel}
            onChange={handleChange('euTaxModel')}
            sx={{ width: fieldWidth }}
            helperText="Wählen Sie hier das EU-Reverse-Charge-Verfahren aus. Die weclapp-IDs unterscheiden sich von Instanz zu Instanz."
          >
            {taxModels.map((tax) => (
              <MenuItem key={tax.id} value={tax.id}>
                {tax.name}
              </MenuItem>
            ))}
          </TextField>,
          <TextField
            select
            fullWidth
            label="Leistungszeitraum der Rechnungen"
            value={fields.servicePeriods}
            onChange={handleChange('servicePeriods')}
            sx={{ width: fieldWidth }}
            helperText="Wählen Sie hier das EU-Reverse-Charge-Verfahren aus. Die weclapp-IDs unterscheiden sich von Instanz zu Instanz."
          >
            <MenuItem value="exactDates">Exakte Daten</MenuItem>
            <MenuItem value="fullMonths">Ganze Monate</MenuItem>
          </TextField>,
        )}

        {formRow(
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={fields.tags}
            sx={{ width: fieldWidth }}
            onChange={handleTagsChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Schlagworte für Rechnungen"
                helperText="Geben Sie Tags für die weclapp-Rechnung ein. Diese ermöglichen eine bessere Filterung."
              />
            )}
          />,
          <TextField
            fullWidth
            label="Kommission"
            value={fields.commission}
            onChange={handleChange('commission')}
            sx={{ width: fieldWidth }}
            helperText="Kommission für die weclapp-Rechnungen"
          />,
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      </CardActions>
    </Card>
  );
};

export default Templates;
